/**
 * Flow Builder Configuration Store
 * Controls language-specific features and UI modes
 * Now integrates with the data-driven validation system
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { getLanguageConfig, preloadLanguageConfigs, setLanguageConfigCache } from '@/validation';

// Default configuration
const DEFAULT_CONFIG = {
  // Track type: 'programming' (C, Java, Python, JS) or 'ai_agent' (LLM-based agents)
  trackType: 'programming',
  
  // Target programming language (fixed by track/practice item in production)
  language: 'c',
  
  // Loaded language configuration from backend
  languageConfig: null,
  
  // Loading state
  isLoadingConfig: false,
  configError: null,
  
  // UI Mode settings
  uiMode: {
    // Show visual condition builder or raw expression input
    conditionBuilder: true,
    
    // Expression input mode: 'guided' (dropdowns) | 'raw' (text) | 'both' (toggle)
    expressionMode: 'both',
    
    // Show code preview panel
    showCodePreview: true,
    
    // Allow students to see generated code
    showGeneratedCode: true,
    
    // Enable validation hints
    showValidationHints: true,
  },
  
  // Practice item specific overrides (set when loading a practice item)
  practiceItemConfig: null,

  // Epic #581 — where the item being built sits: { courseSlug, moduleSlug },
  // from its /progress response (or ?dev=true&module=…). The Input step's
  // inline Add New offers only what that module has taught. Set per visit by
  // the builder page and reset on leaving; never persisted (partialize below).
  itemPlacement: null,
};

const useConfigStore = create(
  persist(
    (set, get) => ({
      ...DEFAULT_CONFIG,
      
      // Load language configuration from backend
      loadLanguageConfig: async (languageId) => {
        const currentLang = get().language;
        const targetLang = languageId || currentLang;
        
        set({ isLoadingConfig: true, configError: null });
        
        try {
          const config = await getLanguageConfig(targetLang);
          
          // Cache for synchronous validation
          setLanguageConfigCache(targetLang, config);
          
          set({ 
            languageConfig: config, 
            language: targetLang,
            isLoadingConfig: false 
          });
          
          return config;
        } catch (error) {
          console.error('Failed to load language config:', error);
          set({ 
            configError: error.message, 
            isLoadingConfig: false 
          });
          return null;
        }
      },
      
      // Get current language config (synchronous, from cache)
      getLanguageConfig: () => {
        const { languageConfig, language } = get();
        
        // Return cached config if available
        if (languageConfig && languageConfig.id === language) {
          return languageConfig;
        }
        
        // Return minimal fallback config
        return {
          id: language,
          name: language.charAt(0).toUpperCase() + language.slice(1),
          keywords: [],
          functions: [],
          logical_operators: language === 'python' ? ['and', 'or', 'not'] : ['&&', '||', '!'],
          comparison_operators: ['==', '!=', '<', '>', '<=', '>='],
          invalid_operators: language === 'python' ? ['&&', '||', '!'] : ['and', 'or', 'not'],
          supports_while: true,
          supports_do_while: language !== 'python',
          supports_for: true,
          supports_for_each: language !== 'c', // C++ supports range-based for
        };
      },
      
      // Check if a specific loop type is supported
      isLoopTypeSupported: (loopType) => {
        const config = get().getLanguageConfig();
        const loopMap = {
          while: config.supports_while,
          doWhile: config.supports_do_while,
          for: config.supports_for,
          forEach: config.supports_for_each,
        };
        return loopMap[loopType] ?? false;
      },
      
      // Get available loop types for current language
      getAvailableLoopTypes: () => {
        const config = get().getLanguageConfig();
        const types = [];
        if (config.supports_while) types.push('while');
        if (config.supports_do_while) types.push('doWhile');
        if (config.supports_for) types.push('for');
        if (config.supports_for_each) types.push('forEach');
        return types;
      },
      
      // Set target language and load its config
      setLanguage: async (language) => {
        set({ language });
        await get().loadLanguageConfig(language);
      },
      
      // Set track type (programming or ai_agent)
      setTrackType: (trackType) => {
        set({ trackType });
      },
      
      // Set UI mode option
      setUiMode: (key, value) => {
        set((state) => ({
          uiMode: {
            ...state.uiMode,
            [key]: value,
          },
        }));
      },
      
      // Toggle condition builder
      toggleConditionBuilder: () => {
        set((state) => ({
          uiMode: {
            ...state.uiMode,
            conditionBuilder: !state.uiMode.conditionBuilder,
          },
        }));
      },
      
      // Set expression mode
      setExpressionMode: (mode) => {
        if (['guided', 'raw', 'both'].includes(mode)) {
          set((state) => ({
            uiMode: {
              ...state.uiMode,
              expressionMode: mode,
            },
          }));
        }
      },
      
      // Load practice item configuration (overrides defaults)
      loadPracticeItemConfig: async (config) => {
        set({ practiceItemConfig: config });
        
        // Apply practice item settings
        if (config?.language) {
          await get().setLanguage(config.language);
        }
        
        if (config?.uiMode) {
          set((state) => ({
            uiMode: { ...state.uiMode, ...config.uiMode },
          }));
        }
      },
      
      // Clear practice item configuration
      clearPracticeItemConfig: () => {
        set({ practiceItemConfig: null });
      },
      
      // Reset to defaults
      // Epic #581 — the item's course + module (null = offer everything).
      setItemPlacement: (placement) => set({ itemPlacement: placement || null }),

      resetConfig: () => {
        set({ ...DEFAULT_CONFIG });
      },
      
      // Preload common language configs
      preloadConfigs: async () => {
        await preloadLanguageConfigs(['c', 'cpp', 'python', 'java', 'javascript']);
      },
    }),
    {
      name: 'flow-builder-config',
      version: 4,
      // ADO #587 — showGeneratedCode is the words-only switch, set per item by
      // the builder page from the server. It must NOT outlive the visit: a
      // Problem Solving item would otherwise leave a later C item words-only.
      partialize: (state) => {
        const { showGeneratedCode, ...uiMode } = state.uiMode || {}; // eslint-disable-line no-unused-vars
        return {
          trackType: state.trackType,
          language: state.language,
          uiMode,
        };
      },
      merge: (persisted, current) => ({
        ...current,
        ...(persisted || {}),
        uiMode: {
          ...current.uiMode,
          ...((persisted && persisted.uiMode) || {}),
          showGeneratedCode: true,
        },
      }),
      // Migrate from old versions
      migrate: (persistedState, version) => {
        if (version < 3) {
          // Force expressionMode to 'both' for all users
          return {
            ...persistedState,
            uiMode: {
              ...persistedState.uiMode,
              expressionMode: 'both',
            },
          };
        }
        if (version < 4) {
          // Add trackType for all users
          return {
            ...persistedState,
            trackType: 'programming',
          };
        }
        return persistedState;
      },
    }
  )
);

export default useConfigStore;
